import React, { useState, useEffect, useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Button } from '@nextui-org/react';
import { COLORS } from 'common/src/utils/colors';
import { MarketData } from 'common/src/utils/types';
import { BitacoraPriceData } from 'common/src/api/market_comments';
import useUserStore from '../../stores/useUserStore';
import { getChartThemeColors, tooltipContainerStyle, tooltipTitleStyle, tooltipLabelStyle, tooltipValueStyle } from '../../utils/chartTheme';
import CaliberFilter from '../filters/CaliberFilter';
import VarietyFilter from '../filters/VarietyFilter';
import useDataStore from '../../stores/useDataStore';
import * as XLSX from 'xlsx';
import { Icon } from '@iconify/react';
import { useTranslation } from '../../hooks/useTranslation';
import BrandDetailModal from '../../modals/BrandDetailModal';
import { buildExporterSeries, isExporterSeries } from './exporterSeries';
import {
  BitacoraMode,
  buildBitacoraSeries,
  isBitacoraSeries,
  renderBitacoraTooltip,
} from './bitacoraSeries';
import ChartDownloadButton from './ChartDownloadButton';

interface VarietyPriceChartProps {
  data: MarketData[];
  currency: 'RMB' | 'USD';
  priceView: 'kg' | 'box';
  topExportersData: MarketData[];
  boxSize?: number;
  maxVarieties?: number; // Límite de variedades a mostrar (undefined = todas)
  dateRangeLabel?: string; // Label opcional para mostrar rango de fechas
  padding?: string; // Padding opcional para el contenedor (ej: 'p-4', 'px-2 py-4')
  visualization?: 'chart' | 'table';
  showTopExporters?: boolean; // Mostrar serie de exportadoras más registradas (default: true)
  compactLayout?: boolean; // Layout compacto para espacios reducidos como el dashboard
  hideExporterLegend?: boolean; // Hide exporter series from legend (for importers app)
  separationType?: 'none' | 'brand'; // Type of separation for user exporter data
  selectedBrands?: string[]; // Selected brands to show when separationType is 'brand'
  bitacoraData?: BitacoraPriceData[]; // Bitácora prices to overlay on chart
  bitacoraMode?: BitacoraMode; // Un punto promedio, o uno por cliente o por etiqueta
  exporterBrandKeys?: string[] | null; // Etiquetas de las exportadoras seleccionadas a separar
}

const VarietyPriceChart: React.FC<VarietyPriceChartProps> = ({
  data,
  currency,
  priceView,
  topExportersData,
  boxSize = 5,
  maxVarieties,
  dateRangeLabel,
  padding = '',
  visualization = 'chart',
  showTopExporters = true,
  compactLayout = false,
  hideExporterLegend = false,
  separationType = 'none',
  selectedBrands = [],
  bitacoraData = [],
  bitacoraMode = 'average',
  exporterBrandKeys = null,
}) => {
  const { t } = useTranslation('market');
  // Inicializar como string vacío para permitir que CaliberFilter seleccione el calibre más común
  const [selectedCaliber, setSelectedCaliber] = useState<string>('');
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [selectedVarieties, setSelectedVarieties] = useState<string[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [userExporterData, setUserExporterData] = useState<any[]>([]);
  const [calibers, setCalibers] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVariety, setSelectedVariety] = useState('');
  const { userProfile, darkMode } = useUserStore();
  const chartColors = getChartThemeColors(darkMode);
  const { selectedSpecies } = useDataStore();
  const userExporter = userProfile?.exporter;

  // Colors for brands when separationType is 'brand'
  const brandColors = [
    '#4CAF50', // green
    '#FF9800', // orange
    '#9C27B0', // purple
    '#00BCD4', // cyan
    '#FF5722', // deep orange
    '#673AB7', // deep purple
    '#009688', // teal
    '#FFC107', // amber
  ];

  const handlePointClick = (variety: string) => {
    setSelectedVariety(variety);
    setIsModalOpen(true);
  };

  // Mostrar TODAS las variedades disponibles en los datos
  const getAllowedVarieties = (data: MarketData[]) => {
    const allVarieties = [
      ...new Set(
        data.map((item) => item.variety?.toLowerCase()).filter(Boolean)
      ),
    ];
    console.log('Usando todas las variedades disponibles:', allVarieties);
    return allVarieties;
  };

  useEffect(() => {
    const groupedData = data?.reduce(
      (acc, item) => {
        const caliber = item.size;
        if (!acc[caliber]) {
          acc[caliber] = [];
        }

        acc[caliber].push(item);

        return acc;
      },
      {} as Record<string, MarketData[]>
    );
    console.log('groupedData', groupedData);

    const sortedCalibers = Object.keys(groupedData).sort((a, b) => {
      const order = ['XL', 'J', '2J', '3J', '4J', '5J'];
      const aIndex = order.indexOf(a);
      const bIndex = order.indexOf(b);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
    console.log('sortedCalibers', sortedCalibers);
    setCalibers(sortedCalibers);

    const prepareChartData = (filteredData: MarketData[]) => {
      // Determinar las variedades permitidas para esta especie/datos
      const allowedVarieties = getAllowedVarieties(filteredData);
      console.log('Variedades permitidas:', allowedVarieties);

      const varietyData = filteredData.reduce(
        (acc, item) => {
          // Verificar si la variedad está permitida
          if (!allowedVarieties.includes(item?.variety?.toLowerCase()))
            return acc;

          if (!acc[item.variety]) {
            acc[item.variety] = {
              prices: [],
              userExporterPrices: [],
              userExporterPricesByBrand: {}, // Para separación por etiqueta
            };
          }
          acc[item.variety].prices.push(item.price_kg);
          if (item.exporter === userExporter?.excel_name) {
            acc[item.variety].userExporterPrices.push(item.price_kg);

            // Si hay separación por etiqueta, también agrupar por brand
            if (separationType === 'brand' && item.brand) {
              if (!acc[item.variety].userExporterPricesByBrand[item.brand]) {
                acc[item.variety].userExporterPricesByBrand[item.brand] = [];
              }
              acc[item.variety].userExporterPricesByBrand[item.brand].push(item.price_kg);
            }
          }
          return acc;
        },
        {} as Record<
          string,
          {
            prices: number[];
            userExporterPrices: number[];
            userExporterPricesByBrand: Record<string, number[]>;
          }
        >
      );

      const sortedVarieties = Object.entries(varietyData)
        .map(
          ([variety, { prices, userExporterPrices }]) => {
            const sortedPrices = prices.sort((a, b) => a - b);
            const q1 = sortedPrices[Math.floor(sortedPrices.length / 4)];
            const median = sortedPrices[Math.floor(sortedPrices.length / 2)];
            const q3 = sortedPrices[Math.floor((3 * sortedPrices.length) / 4)];
            const min = sortedPrices[0];
            const max = sortedPrices[sortedPrices.length - 1];

            // User exporter calculations
            const userExporterAvg =
              userExporterPrices.length > 0
                ? userExporterPrices.reduce((a, b) => a + b, 0) /
                userExporterPrices.length
                : null;

            let userExporterPercentile = null;
            if (userExporterAvg !== null) {
              const lowerPrices = sortedPrices.filter(
                (price) => price <= userExporterAvg
              );
              userExporterPercentile =
                (lowerPrices.length / sortedPrices.length) * 100;
            }

            // Calculate brands data for tooltip (when separationType === 'brand')
            const brandsData = selectedBrands.map((brand) => {
              const brandPrices = varietyData[variety]?.userExporterPricesByBrand[brand] || [];
              const brandAvg =
                brandPrices.length > 0
                  ? brandPrices.reduce((a, b) => a + b, 0) / brandPrices.length
                  : null;

              let brandPercentile = null;
              if (brandAvg !== null) {
                const lowerPrices = sortedPrices.filter((price) => price <= brandAvg);
                brandPercentile = (lowerPrices.length / sortedPrices.length) * 100;
              }

              return {
                brand,
                avg: brandAvg,
                percentile: brandPercentile,
                records: brandPrices.length,
              };
            });

            return {
              x: variety,
              y: [min, q1, median, q3, max],
              yBox: [min, q1, median, q3, max],
              userExporterAvg,
              userExporterAvgBox: userExporterAvg,
              userExporterPercentile,
              totalRecords: prices.length,
              userExporterRecords: userExporterPrices.length,
              brandsData, // Add brands data for tooltip
              separationType, // Add separationType for tooltip logic
            };
          }
        )
        .sort((a, b) => b.totalRecords - a.totalRecords) // Ordenar por cantidad de registros (mayor a menor)
        .filter((item) =>
          selectedVarieties.length === 0 ||
          selectedVarieties.includes(item.x.toUpperCase())
        ) // Filtrar por variedades seleccionadas
        .slice(0, maxVarieties); // Limitar al top N si maxVarieties está definido

      return sortedVarieties;
    };

    // Solo procesar los datos si tenemos un calibre seleccionado
    if (selectedCaliber) {
      console.log(`Procesando datos para el calibre: ${selectedCaliber}`);
      const filteredData = groupedData[selectedCaliber] || [];
      const newChartData = prepareChartData(filteredData);
      setChartData(newChartData);
      setUserExporterData(
        newChartData.map((item) => ({
          x: item.x,
          y: item.userExporterAvg !== null ? item.userExporterAvg : null,
        }))
      );
    } else {
      console.log('No hay calibre seleccionado todavía');
    }
  }, [data, selectedCaliber, selectedVarieties, selectedSpecies, maxVarieties]);

  // Manejar cambio de calibre
  const handleCaliberChange = (value: string[]) => {
    if (value.length > 0) {
      console.log(`Calibre seleccionado: ${value[0]}`);
      setSelectedCaliber(value[0]);
    }
  };

  // Manejar cambio de variedades
  const handleVarietiesChange = (value: string[]) => {
    console.log(`Variedades seleccionadas:`, value);
    setSelectedVarieties(value);
  };

  // Un punto por exportadora seleccionada, no un promedio de todas.
  const varietiesShown = chartData.map((item) => item.x);
  const caliberData = data.filter((item) => item.size === selectedCaliber);
  const exporterSeries = showTopExporters
    ? buildExporterSeries({
        records: topExportersData,
        categories: varietiesShown,
        categoryOf: (item) =>
          item.size === selectedCaliber && varietiesShown.includes(item.variety)
            ? item.variety
            : null,
        marketPrices: caliberData.reduce(
          (acc, item) => {
            if (!acc[item.variety]) acc[item.variety] = [];
            acc[item.variety].push(item.price_kg);
            return acc;
          },
          {} as Record<string, number[]>
        ),
        excludeExporter: userExporter?.excel_name,
        brandKeys: exporterBrandKeys,
        noBrandLabel: t('Sin etiqueta'),
      })
    : [];

  // Excel export function
  const exportToExcel = () => {
    const sign = currency === 'USD' ? '$' : '¥';

    const excelData: any[][] = [];

    // Headers: Métrica + variety names
    const headers = ['Métrica', ...chartData.map((item) => item.x)];
    excelData.push(headers);

    // Registros row
    excelData.push([
      'Registros',
      ...chartData.map((item) => item.totalRecords),
    ]);

    // Mínimo row
    excelData.push([
      `Mínimo (${sign} ${currency}${unit})`,
      ...chartData.map((item) => item.y[0]),
    ]);

    // Q1 row
    excelData.push([
      `Q1 (${sign} ${currency}${unit})`,
      ...chartData.map((item) => item.y[1]),
    ]);

    // Mediana row
    excelData.push([
      `Mediana (${sign} ${currency}${unit})`,
      ...chartData.map((item) => item.y[2]),
    ]);

    // Q3 row
    excelData.push([
      `Q3 (${sign} ${currency}${unit})`,
      ...chartData.map((item) => item.y[3]),
    ]);

    // Máximo row
    excelData.push([
      `Máximo (${sign} ${currency}${unit})`,
      ...chartData.map((item) => item.y[4]),
    ]);

    // User exporter row (if available)
    if (userExporter && separationType === 'brand') {
      // Una fila por etiqueta propia, como en el gráfico
      series
        .filter((s) => s.isBrandSeries)
        .forEach((brandSerie) => {
          excelData.push([
            brandSerie.name,
            ...brandSerie.data.map((point) =>
              point.y !== null && point.y !== undefined ? point.y : '-'
            ),
          ]);
          excelData.push([
            `${brandSerie.name} ${t('Percentil')}`,
            ...brandSerie.data.map((point) =>
              point.brandPercentile !== null && point.brandPercentile !== undefined
                ? `${point.brandPercentile.toFixed(0)}%`
                : '-'
            ),
          ]);
        });
    } else if (userExporter) {
      excelData.push([
        `${userExporter.excel_name} (${sign} ${currency}${unit})`,
        ...chartData.map((item) =>
          item.userExporterAvg !== null ? item.userExporterAvg : '-'
        ),
      ]);

      excelData.push([
        `${userExporter.excel_name} Percentil (%)`,
        ...chartData.map((item) =>
          item.userExporterPercentile !== null
            ? item.userExporterPercentile
            : '-'
        ),
      ]);
    }

    // Una fila por exportadora seleccionada
    exporterSeries.forEach((exporterSerie) => {
      excelData.push([
        `${exporterSerie.name} (${sign} ${currency}${unit})`,
        ...exporterSerie.data.map((point) =>
          point.y !== null ? point.y : '-'
        ),
      ]);
    });

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [{ wch: 30 }, ...chartData.map(() => ({ wch: 12 }))];

    XLSX.utils.book_append_sheet(wb, ws, 'Dispersión por variedad');
    XLSX.writeFile(wb, `Dispersión_Variedad_${selectedCaliber}.xlsx`);
  };

  // Componente TableView
  const TableView = () => {
    const sign = currency === 'USD' ? '$' : '¥';
    const unit = priceView === 'kg' ? '/kg' : `/${boxSize}kg box`;

    return (
      <div className='mt-4'>
        <div className='overflow-x-auto'>
          <table className='min-w-full border-collapse'>
            <thead>
              <tr className='bg-default-100'>
                <th className='border px-4 py-2 sticky left-0 bg-default-100 z-10'>
                  Métrica
                </th>
                {chartData.map((varietyData) => (
                  <th
                    key={varietyData.x}
                    className='border px-4 py-2 text-center min-w-[100px]'
                  >
                    {varietyData.x}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Registros row */}
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  Registros
                </td>
                {chartData.map((varietyData) => (
                  <td
                    key={varietyData.x}
                    className='border px-4 py-2 text-center'
                  >
                    {varietyData.totalRecords}
                  </td>
                ))}
              </tr>

              {/* Mínimo row */}
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  Mínimo
                </td>
                {chartData.map((varietyData) => (
                  <td
                    key={varietyData.x}
                    className='border px-4 py-2 text-center'
                  >
                    {sign}
                    {varietyData.y[0].toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Q1 row */}
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  Q1
                </td>
                {chartData.map((varietyData) => (
                  <td
                    key={varietyData.x}
                    className='border px-4 py-2 text-center'
                  >
                    {sign}
                    {varietyData.y[1].toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Mediana row */}
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  Mediana
                </td>
                {chartData.map((varietyData) => (
                  <td
                    key={varietyData.x}
                    className='border px-4 py-2 text-center'
                  >
                    {sign}
                    {varietyData.y[2].toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Q3 row */}
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  Q3
                </td>
                {chartData.map((varietyData) => (
                  <td
                    key={varietyData.x}
                    className='border px-4 py-2 text-center'
                  >
                    {sign}
                    {varietyData.y[3].toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Máximo row */}
              <tr>
                <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                  Máximo
                </td>
                {chartData.map((varietyData) => (
                  <td
                    key={varietyData.x}
                    className='border px-4 py-2 text-center'
                  >
                    {sign}
                    {varietyData.y[4].toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* User exporter row */}
              {separationType === 'brand' &&
                series
                  .filter((s) => s.isBrandSeries)
                  .map((brandSerie) => (
                    <tr key={brandSerie.name}>
                      <td className='border px-4 py-2 font-medium sticky left-0 bg-green-50 z-10'>
                        <span className='flex items-center gap-2'>
                          <span
                            className='inline-block h-2.5 w-2.5 rounded-full'
                            style={{ backgroundColor: brandSerie.color }}
                          />
                          {brandSerie.name}
                        </span>
                      </td>
                      {brandSerie.data.map((point) => (
                        <td
                          key={point.x}
                          className='border px-4 py-2 text-center bg-green-50 font-semibold text-green-700'
                        >
                          {point.y !== null && point.y !== undefined
                            ? `${sign}${point.y.toFixed(2)} · ${point.brandPercentile?.toFixed(0) ?? '-'}%`
                            : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
              {userExporter && separationType !== 'brand' && (
                <>
                  <tr>
                    <td className='border px-4 py-2 font-medium sticky left-0 bg-green-50 z-10'>
                      {userExporter.excel_name}
                    </td>
                    {chartData.map((varietyData) => (
                      <td
                        key={varietyData.x}
                        className='border px-4 py-2 text-center bg-green-50 font-semibold text-green-700'
                      >
                        {varietyData.userExporterAvg !== null
                          ? `${sign}${varietyData.userExporterAvg.toFixed(2)}`
                          : '-'}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className='border px-4 py-2 font-medium sticky left-0 bg-green-50 z-10'>
                      Percentil
                    </td>
                    {chartData.map((varietyData) => (
                      <td
                        key={varietyData.x}
                        className='border px-4 py-2 text-center bg-green-50 font-semibold text-green-700'
                      >
                        {varietyData.userExporterPercentile !== null
                          ? `${varietyData.userExporterPercentile.toFixed(0)}%`
                          : '-'}
                      </td>
                    ))}
                  </tr>
                </>
              )}

              {/* Una fila por exportadora seleccionada */}
              {exporterSeries.map((exporterSerie) => (
                <tr key={exporterSerie.name}>
                  <td className='border px-4 py-2 font-medium sticky left-0 bg-background z-10'>
                    <span className='flex items-center gap-2'>
                      <span
                        className='inline-block h-2.5 w-2.5 rounded-full'
                        style={{ backgroundColor: exporterSerie.color }}
                      />
                      {exporterSerie.name}
                    </span>
                  </td>
                  {exporterSerie.data.map((point) => (
                    <td
                      key={point.x}
                      className='border px-4 py-2 text-center font-semibold'
                    >
                      {point.y !== null ? `${sign}${point.y.toFixed(2)}` : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Build dynamic colors array based on series
  const buildColorsArray = () => {
    const colors = [COLORS.cherry]; // Box plot always first

    if (separationType === 'none') {
      colors.push(COLORS.lightGreen); // User exporter (green)
    } else if (separationType === 'brand') {
      // Add brand colors
      selectedBrands.forEach((_, index) => {
        colors.push(brandColors[index % brandColors.length]);
      });
    }

    // Un color por exportadora seleccionada.
    exporterSeries.forEach((s) => colors.push(s.color));

    // Un morado por cada serie de Bitácora dibujada.
    series
      .filter(isBitacoraSeries)
      .forEach((s) => colors.push(s.color));

    return colors;
  };

  // Build series based on separationType
  const buildSeries = () => {
    const seriesArray: any[] = [
      {
        name: t('MERCADO'),
        type: 'boxPlot',
        data: chartData,
      },
    ];

    if (separationType === 'none') {
      // Single user exporter series (green point)
      seriesArray.push({
        name: userExporter?.excel_name?.toUpperCase(),
        type: 'scatter',
        data: chartData.map((item) => ({
          x: item.x,
          y: item.userExporterAvg,
          userExporterPercentile: item.userExporterPercentile,
          records: item.userExporterRecords,
        })),
      });
    } else if (separationType === 'brand' && selectedBrands.length > 0) {
      // Multiple brand series (one per selected brand)
      selectedBrands.forEach((brand, index) => {
        const brandData = chartData.map((item: any) => {
          // Access the variety data from filteredData to get brand prices
          const variety = item.x;
          const filteredVarietyData = data.filter(
            d => d.variety === variety && d.size === selectedCaliber && d.brand === brand && d.exporter === userExporter?.excel_name
          );

          if (filteredVarietyData.length === 0) {
            return { x: variety, y: null };
          }

          const brandPrices = filteredVarietyData.map(d => d.price_kg);
          const brandAvg = brandPrices.reduce((a, b) => a + b, 0) / brandPrices.length;

          // Calculate percentile
          const allPrices = data
            .filter(d => d.variety === variety && d.size === selectedCaliber)
            .map(d => d.price_kg)
            .sort((a, b) => a - b);
          const lowerPrices = allPrices.filter(price => price <= brandAvg);
          const brandPercentile = (lowerPrices.length / allPrices.length) * 100;

          return {
            x: variety,
            y: brandAvg,
            brandPercentile,
            brandRecords: brandPrices.length,
            brandName: brand,
          };
        });

        seriesArray.push({
          name: [userExporter?.excel_name, brand].filter(Boolean).join(' · ').toUpperCase(),
          type: 'scatter',
          data: brandData,
          color: brandColors[index % brandColors.length],
          isBrandSeries: true,
        });
      });
    }

    seriesArray.push(...exporterSeries);

    // Puntos de Bitácora: promedio ponderado por bultos, o uno por cliente o por etiqueta.
    seriesArray.push(
      ...buildBitacoraSeries({
        records: bitacoraData,
        categories: chartData.map((item) => item.x),
        // Del calibre elegido; los registros sin calibre aplican a todos.
        categoryOf: (b) =>
          b.variety &&
          (!b.caliber ||
            b.caliber.toUpperCase() === selectedCaliber.toUpperCase())
            ? b.variety.toUpperCase()
            : null,
        mode: bitacoraMode,
        label: t('Bitácora'),
        noClientLabel: t('Sin cliente'),
        noLabelLabel: t('Sin etiqueta o varias'),
      })
    );

    return seriesArray;
  };

  const series = buildSeries();

  const options: ApexOptions = {
    chart: {
      type: 'boxPlot',
      height: 350,
      toolbar: { show: false },
      foreColor: chartColors.foreColor,
      zoom: {
        enabled: false,
      },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const variety = chartData[config.dataPointIndex]?.x;

          // Check if user exporter has data for this variety
          if (userExporter && variety) {
            const hasUserData = chartData[config.dataPointIndex]?.userExporterAvg !== null &&
                               chartData[config.dataPointIndex]?.userExporterAvg !== undefined;

            // Only open modal if clicking on box plot (seriesIndex 0) and user has data
            if (config.seriesIndex === 0 && hasUserData) {
              handlePointClick(variety);
            }
            // Also keep the click on user exporter point
            else if (
              config.seriesIndex === 1 &&
              !isExporterSeries(series[1])
            ) {
              handlePointClick(variety);
            }
          }
        },
      },
    },
    colors: buildColorsArray(),
    markers: {
      size: Array(series.length).fill(0).map((_, i) => i === 0 ? 0 : 5), // 0 for boxPlot, 5 for scatter
      colors: buildColorsArray(),
      hover: {
        size: 7,
      },
    },
    stroke: {
      colors: [darkMode ? '#ffffff' : '#000000'],
    },
    plotOptions: {
      boxPlot: {
        colors: {
          upper: COLORS.cherry,
          lower: COLORS.cherry,
        },
      },
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
        },
      },
    },
    legend: {
      show: !hideExporterLegend,
    },
    xaxis: {
      categories: chartData.map((item) => item.x),
      title: {
        text: t('Variedades'),
      },
      labels: {
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: true,
        trim: true,
        style: {
          fontSize: window.innerWidth < 768 ? '10px' : '12px',
        },
      },
      tickPlacement: 'on',
    },
    yaxis: {
      title: {
        text: `${t('Precio')} (${currency}/${priceView})`,
      },
      labels: {
        formatter: function (value) {
          const sign = currency === 'USD' ? '$' : '¥';
          return Math.round(value) + ' ' + sign;
        },
      },
    },
    tooltip: {
      shared: false,
      intersect: true,
      followCursor: false,
      custom: function ({ seriesIndex, dataPointIndex, w }) {
        const data =
          w.globals?.initialSeries?.[seriesIndex]?.data?.[dataPointIndex];
        if (!data) return '';

        const unit = priceView === 'kg' ? '/kg' : `/${boxSize}kg box`;

        if (data.isBitacora) {
          return renderBitacoraTooltip({
            point: data,
            categoryLabel: `${data.x} · ${t('Calibre')} ${selectedCaliber}`,
            currency,
            unit,
            darkMode,
            t,
          });
        }

        // Check if this is a boxplot series (market data)
        if (seriesIndex === 0) {
          const priceData = priceView === 'kg' ? data.y : data.yBox;
          const userExporterPrice =
            priceView === 'kg' ? data.userExporterAvg : data.userExporterAvgBox;
          const percentileFormatted =
            data.userExporterPercentile != null
              ? data.userExporterPercentile.toFixed(0)
              : 'N/A';

          return `
          <div style="${tooltipContainerStyle(darkMode)}">
            <div style="${tooltipTitleStyle(darkMode)} margin-bottom: 2px;">
              ${data.x ?? ''}
            </div>
            <div style="font-size: 12px; text-align: center; color: ${chartColors.tooltipText}; margin-bottom: 4px;">
              ${data.totalRecords ?? 0} ${t('registros')}
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span style="color: ${chartColors.tooltipText};">${t('Máximo')}:</span>
              <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${priceData?.[4]?.toFixed(2) ?? 0
          } ${currency}${unit}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
              <span style="color: ${chartColors.tooltipText};">${t('Mediana')}:</span>
              <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${priceData?.[2]?.toFixed(2) ?? 0
          } ${currency}${unit}</span>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
              <span style="color: ${chartColors.tooltipText};">${t('Mínimo')}:</span>
              <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${priceData?.[0]?.toFixed(2) ?? 0
          } ${currency}${unit}</span>
            </div>
            ${data.separationType === 'brand' && data.brandsData && data.brandsData.length > 0
            ? `
              <div style="margin-top: 8px; border-top: 1px solid ${chartColors.tooltipBorder}; padding-top: 8px;">
                <div style="font-size: 14px; font-weight: bold; color: ${chartColors.tooltipTitle}; margin-bottom: 6px; text-align: center;">
                  ${t('Etiquetas seleccionadas')}
                </div>
                ${data.brandsData.map((brandData, index) => {
                  const brandColor = brandColors[index % brandColors.length];
                  return brandData.avg !== null
                    ? `
                      <div style="margin-bottom: 6px; border-left: 3px solid ${brandColor}; padding-left: 8px;">
                        <div style="display: flex; justify-content: space-between; margin-bottom: 2px;">
                          <span style="color: ${brandColor}; font-weight: bold;">${brandData.brand}</span>
                          <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${brandData.avg.toFixed(2)} ${currency}${unit}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; font-size: 12px; color: ${chartColors.tooltipText};">
                          <span>${t('Percentil')}: ${brandData.percentile !== null ? brandData.percentile.toFixed(0) : 'N/A'}%</span>
                          <span>${brandData.records} ${t('registros')}</span>
                        </div>
                      </div>
                    `
                    : `
                      <div style="margin-bottom: 6px; border-left: 3px solid ${brandColor}; padding-left: 8px;">
                        <div style="font-size: 12px; color: ${chartColors.tooltipText};">
                          ${brandData.brand}: ${t('No hay datos para esta variedad')}
                        </div>
                      </div>
                    `;
                }).join('')}
              </div>
            `
            : ''
          }
            ${userExporterPrice != null && data.separationType !== 'brand'
            ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span style="color: #4CAF50; font-weight: bold;">${userExporter?.excel_name ?? ''
            } - ${t('Percentil')} ${percentileFormatted}%</span>
                <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${userExporterPrice?.toFixed(2) ?? 0
            } ${currency}${unit}</span>
              </div>
              <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: left;">
                ${data.userExporterRecords ?? 0} ${t('registros')} de ${userExporter?.excel_name ?? ''
            }
              </div>
              <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: right;">
                ${userExporter?.excel_name ?? ''
            } ${t('está sobre el')} ${percentileFormatted}% ${t('de los datos para esta variedad')}
              </div>
            `
            : data.separationType !== 'brand'
              ? `
                <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                  ${t('No hay datos de')} ${userExporter?.excel_name ?? ''
            } ${t('para esta variedad')}
                </div>
              `
              : ''
          }
            ${exporterSeries.length > 0
            ? `
              <div style="margin-top: 8px; border-top: 1px solid ${chartColors.tooltipBorder}; padding-top: 8px;">
                <div style="font-size: 14px; font-weight: bold; color: ${chartColors.tooltipTitle}; margin-bottom: 6px; text-align: center;">
                  ${t('Exportadoras Seleccionadas')}
                </div>
                ${exporterSeries.some((s) => s.data[dataPointIndex]?.y != null)
              ? exporterSeries
                .filter((s) => s.data[dataPointIndex]?.y != null)
                .map(
                  (s) => `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: ${s.color}; font-weight: bold;">${s.name}</span>
                    <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${s.data[dataPointIndex].y?.toFixed(2)} ${currency}${unit}</span>
                  </div>
                `
                )
                .join('')
              : `
                  <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                    ${t('No hay datos para esta variedad')}
                  </div>
                `
            }
              </div>
            `
            : ''
          }
          </div>
        `;
        }

        // Brand series tooltip (individual brand point)
        if (data.brandName) {
          const brandPrice = data.y;
          const brandPercentile = data.brandPercentile !== null ? data.brandPercentile.toFixed(0) : 'N/A';
          const brandRecords = data.brandRecords || 0;

          return `
            <div style="${tooltipContainerStyle(darkMode)}">
              <div style="${tooltipTitleStyle(darkMode)} margin-bottom: 2px;">
                ${data.brandName}
              </div>
              <div style="text-align: center; font-size: 14px; color: ${chartColors.tooltipText}; margin-bottom: 8px;">
                ${data.x}
              </div>
              ${brandPrice !== null
                ? `
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: ${chartColors.tooltipText};">${t('Precio')}:</span>
                    <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${brandPrice.toFixed(2)} ${currency}${unit}</span>
                  </div>
                  <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                    <span style="color: ${chartColors.tooltipText};">${t('Percentil')}:</span>
                    <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${brandPercentile}%</span>
                  </div>
                  <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                    ${brandRecords} ${t('registros')}
                  </div>
                `
                : `
                  <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                    ${t('No hay datos para esta variedad')}
                  </div>
                `
              }
            </div>
          `;
        }

        // User exporter or top exporters series (when separationType === 'none')
        const price = data.y;
        const percentile = data.userExporterPercentile || data.percentile;
        const records = data.records;
        const seriesName = w.globals.seriesNames[seriesIndex];

        return `
          <div style="${tooltipContainerStyle(darkMode)}">
            <div style="${tooltipTitleStyle(darkMode)} margin-bottom: 2px;">
              ${seriesName}
            </div>
            <div style="text-align: center; font-size: 14px; color: ${chartColors.tooltipText}; margin-bottom: 8px;">
              ${data.x}
            </div>
            ${price !== null && price !== undefined
              ? `
                <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                  <span style="color: ${chartColors.tooltipText};">${t('Precio')}:</span>
                  <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${price.toFixed(2)} ${currency}${unit}</span>
                </div>
                ${percentile !== null && percentile !== undefined
                  ? `
                    <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                      <span style="color: ${chartColors.tooltipText};">${t('Percentil')}:</span>
                      <span style="font-weight: bold; color: ${chartColors.tooltipValue}; margin-left: 10px;">${percentile.toFixed(0)}%</span>
                    </div>
                  `
                  : ''
                }
                ${records !== null && records !== undefined
                  ? `
                    <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                      ${records} ${t('registros')}
                    </div>
                  `
                  : ''
                }
              `
              : `
                <div style="font-size: 12px; color: ${chartColors.tooltipText}; text-align: center;">
                  ${t('No hay datos para esta variedad')}
                </div>
              `
            }
          </div>
        `;
      },
    },
  };

  return (
    <div>
      {compactLayout ? (
        // Layout compacto para dashboard - columna en mobile, 2 columnas en tablet+
        <div className='flex flex-col gap-3 mt-4 mx-4'>
          <div className='flex flex-col'>
            <div className='flex items-center gap-2'>
              <h3 className='text-lg font-bold'>{t('Dispersión por variedad')}</h3>
              {visualization === 'chart' && (
                <ChartDownloadButton
                  chartRef={chartContainerRef}
                  title={t('Dispersión por variedad')}
                  subtitle={`${selectedCaliber} · ${chartData.reduce((sum, item) => sum + item.totalRecords, 0)} ${t('registros')}`}
                />
              )}
            </div>
            {dateRangeLabel && (
              <span className='text-sm text-default-500'>{dateRangeLabel}</span>
            )}
            <span className='text-sm text-default-500'>
              {chartData.reduce((sum, item) => sum + item.totalRecords, 0)}{' '}
              {t('registros')}
            </span>
          </div>

          <div className='flex flex-col sm:flex-row sm:flex-wrap gap-2'>
            {visualization === 'table' && chartData.length > 0 && (
              <Button
                color='success'
                onClick={exportToExcel}
                startContent={
                  <Icon icon='mdi:microsoft-excel' className='text-xl' />
                }
                className='h-12 w-full sm:w-[calc(50%-0.25rem)]'
              >
                {t('Exportar a Excel')}
              </Button>
            )}
            {selectedCaliber && (
              <VarietyFilter
                data={data.filter(item => item.size === selectedCaliber)}
                selectedVariety={selectedVarieties}
                onSelectionChange={handleVarietiesChange}
                selectionMode='multiple'
                label={t('Variedades')}
                className='w-64 max-w-64'
                size='sm'
                topN={5}
                showCounts={false}
                allowAllVarieties={true}
              />
            )}
            <CaliberFilter
              data={data}
              selectedCaliber={selectedCaliber}
              onSelectionChange={handleCaliberChange}
              hideAllOption={true}
              className='w-32 max-w-32'
              size='sm'
            />
          </div>
        </div>
      ) : (
        // Layout normal - filtros a la derecha en pantallas grandes
        <div className='flex flex-col md:flex-row justify-between items-start md:items-center mt-4 mx-4 gap-4'>
          <div className='flex flex-col'>
            <div className='flex items-center gap-2'>
              <h3 className='text-lg font-bold'>{t('Dispersión por variedad')}</h3>
              {visualization === 'chart' && (
                <ChartDownloadButton
                  chartRef={chartContainerRef}
                  title={t('Dispersión por variedad')}
                  subtitle={`${selectedCaliber} · ${chartData.reduce((sum, item) => sum + item.totalRecords, 0)} ${t('registros')}`}
                />
              )}
            </div>
            {dateRangeLabel && (
              <span className='text-sm text-default-500'>{dateRangeLabel}</span>
            )}
            <span className='text-sm text-default-500'>
              {chartData.reduce((sum, item) => sum + item.totalRecords, 0)}{' '}
              {t('registros')}
            </span>
          </div>

          <div className='flex flex-col sm:flex-row gap-2 items-stretch sm:items-end w-full md:w-auto'>
            {visualization === 'table' && chartData.length > 0 && (
              <Button
                color='success'
                onClick={exportToExcel}
                startContent={
                  <Icon icon='mdi:microsoft-excel' className='text-xl' />
                }
                className='h-12'
              >
                {t('Exportar a Excel')}
              </Button>
            )}
            {selectedCaliber && (
              <VarietyFilter
                data={data.filter(item => item.size === selectedCaliber)}
                selectedVariety={selectedVarieties}
                onSelectionChange={handleVarietiesChange}
                selectionMode='multiple'
                label={t('Variedades')}
                className='w-64 max-w-64'
                size='sm'
                topN={5}
                showCounts={false}
                allowAllVarieties={true}
              />
            )}
            <CaliberFilter
              data={data}
              selectedCaliber={selectedCaliber}
              onSelectionChange={handleCaliberChange}
              hideAllOption={true}
              className='w-32 max-w-32'
              size='sm'
            />
          </div>
        </div>
      )}
      <div className={padding}>
        {chartData.length > 0 ? (
          visualization === 'chart' ? (
            <div ref={chartContainerRef}>
              <ReactApexChart
                options={options}
                series={series}
                type='boxPlot'
                height={350}
              />
            </div>
          ) : (
            <TableView />
          )
        ) : (
          <div className='text-center p-10 text-default-500'>
            {selectedCaliber
              ? `${t('No hay datos de variedades disponibles para el calibre')} ${selectedCaliber}`
              : t('Seleccione un calibre para ver los datos')}
          </div>
        )}
      </div>

      {/* Brand Detail Modal */}
      {userExporter && (
        <BrandDetailModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={data}
          caliber={selectedCaliber}
          variety={selectedVariety}
          exporterName={userExporter.excel_name}
          selectedCurrency={currency}
          priceView={priceView}
          boxSize={boxSize}
        />
      )}
    </div>
  );
};

export default VarietyPriceChart;
